import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import styled from "styled-components";

interface VideoIllustrationProps {
  width?: number;
  height?: number;
  duration?: number; // Animation duration in seconds
  loop?: boolean;
  autoPlay?: boolean;
  play?: boolean; // External control for play/pause
  className?: string;
  children?: React.ReactNode;
  constainerRef?: React.RefObject<HTMLDivElement>;
  videoSrc?: string;
  posterImage?: string; // Fallback image when video can't autoplay
}

export interface VideoIllustrationRef {
  play: () => void;
  pause: () => void;
  reset: () => void;
  toggle: () => void;
  isPlaying: boolean;
  currentTime: number;
}

const VideoContainer = styled.div<{ width?: number; height?: number }>`
  position: relative;
  width: 100%;
  overflow: visible;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  /* Prevent gray lines on Chrome Windows */
  transform: translateZ(0);
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: auto;
  /* Prevent gray lines and rendering issues on Chrome Windows */
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  outline: none;
  border: none;
  /* Remove will-change: contents as it can cause rendering issues */
  will-change: auto;
  /* Ensure smooth rendering */
  -webkit-transform: translateZ(0);
  -webkit-font-smoothing: antialiased;
`;

const FallbackImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: auto;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  outline: none;
  border: none;
`;

const VideoIllustration = forwardRef<
  VideoIllustrationRef,
  VideoIllustrationProps
>(
  (
    {
      width = 300,
      height = 300,
      duration = 5, // 5 seconds for full animation
      loop = true,
      autoPlay = true,
      play,
      className,
      children,
      constainerRef,
      videoSrc,
      posterImage,
    },
    ref
  ) => {
    const [isPlaying, setIsPlaying] = useState(false); // Start with false for iOS compatibility
    const [currentTime, setCurrentTime] = useState(0);
    const [canAutoPlay, setCanAutoPlay] = useState(false);
    const [showFallback, setShowFallback] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hasTriedAutoPlay = useRef(false);
    const isUserInitiated = useRef(false);

    // Expose methods through ref
    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          isUserInitiated.current = true; // Mark as user initiated
          setIsPlaying(true);
          setShowFallback(false); // Hide fallback when trying to play
          if (videoRef.current) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setCanAutoPlay(true);
                  setShowFallback(false);
                })
                .catch((error) => {
                  console.log("Play was prevented:", error);
                  setIsPlaying(false);
                  setCanAutoPlay(false);
                  // Show fallback image if available and user initiated
                  if (posterImage && isUserInitiated.current) {
                    setShowFallback(true);
                  }
                });
            }
          }
        },
        pause: () => {
          setIsPlaying(false);
          videoRef.current?.pause();
        },
        reset: () => {
          setCurrentTime(0);
          setIsPlaying(false);
          if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.pause();
          }
        },
        toggle: () => {
          if (isPlaying) {
            setIsPlaying(false);
            videoRef.current?.pause();
          } else {
            setIsPlaying(true);
            setShowFallback(false); // Hide fallback when trying to play
            if (videoRef.current) {
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    setCanAutoPlay(true);
                    setShowFallback(false);
                  })
                  .catch((error) => {
                    console.log("Play was prevented:", error);
                    setIsPlaying(false);
                    setCanAutoPlay(false);
                    // Show fallback image if available
                    if (posterImage) {
                      setShowFallback(true);
                    }
                  });
              }
            }
          }
        },
        isPlaying,
        currentTime,
      }),
      [isPlaying, currentTime]
    );

    // Handle external play prop
    useEffect(() => {
      if (play !== undefined) {
        if (play) {
          setIsPlaying(true);
          setShowFallback(false); // Hide fallback when trying to play
          if (videoRef.current) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setCanAutoPlay(true);
                  setShowFallback(false);
                })
                .catch((error) => {
                  console.log("External play was prevented:", error);
                  setIsPlaying(false);
                  setCanAutoPlay(false);
                  // Show fallback image if available
                  if (posterImage) {
                    setShowFallback(true);
                  }
                });
            }
          }
        } else {
          setIsPlaying(false);
          videoRef.current?.pause();
        }
      }
    }, [play, posterImage]);

    // Handle video events
    const handleTimeUpdate = useCallback(() => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
    }, []);

    const handleEnded = useCallback(() => {
      if (loop) {
        videoRef.current?.play().catch(() => {
          // Silently handle play promise rejection on iOS
        });
      } else {
        setIsPlaying(false);
      }
    }, [loop]);

    const handlePlay = useCallback(() => {
      setIsPlaying(true);
    }, []);

    const handlePause = useCallback(() => {
      setIsPlaying(false);
    }, []);

    // Try autoplay when video loads
    const handleLoadedData = useCallback(() => {
      if (autoPlay && videoRef.current && !hasTriedAutoPlay.current) {
        hasTriedAutoPlay.current = true;
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
              setCanAutoPlay(true);
              setShowFallback(false);
            })
            .catch(() => {
              // iOS/Chrome may block autoplay, this is expected
              setIsPlaying(false);
              setCanAutoPlay(false);
              setShowFallback(true);
            });
        }
      }
    }, [autoPlay]);

    // Additional attempt for iOS compatibility
    const handleCanPlay = useCallback(() => {
      if (
        autoPlay &&
        videoRef.current &&
        !canAutoPlay &&
        !hasTriedAutoPlay.current
      ) {
        hasTriedAutoPlay.current = true;
        // Small delay to help with iOS Chrome compatibility
        setTimeout(() => {
          if (videoRef.current) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setIsPlaying(true);
                  setCanAutoPlay(true);
                  setShowFallback(false);
                })
                .catch(() => {
                  setIsPlaying(false);
                  setCanAutoPlay(false);
                  // Show fallback only after multiple autoplay failures and if we have a poster
                  // This gives time for the user to interact first
                  if (posterImage) {
                    setTimeout(() => {
                      // Final check - only show fallback if still not playing and user hasn't interacted
                      if (!isPlaying && !canAutoPlay) {
                        setShowFallback(true);
                      }
                    }, 2000); // Wait 2 seconds before showing fallback
                  }
                });
            }
          }
        }, 100);
      }
    }, [autoPlay, canAutoPlay, posterImage, isPlaying]);

    // Set video duration if provided
    useEffect(() => {
      if (videoRef.current && duration) {
        videoRef.current.addEventListener("loadedmetadata", () => {
          if (videoRef.current) {
            videoRef.current.currentTime = 0;
          }
        });
      }
    }, [duration]);

    // Reset autoplay attempt when video source changes
    useEffect(() => {
      hasTriedAutoPlay.current = false;
      isUserInitiated.current = false;
      setCanAutoPlay(false);
      setIsPlaying(false);
      setShowFallback(false);
    }, [videoSrc]);

    return (
      <VideoContainer
        width={width}
        height={height}
        className={className}
        ref={constainerRef}
      >
        <VideoElement
          ref={videoRef}
          src={videoSrc}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onPlay={handlePlay}
          onPause={handlePause}
          onLoadedData={handleLoadedData}
          onCanPlay={handleCanPlay}
          loop={loop}
          muted
          playsInline
          autoPlay={true}
          preload="auto"
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          controls={false}
          disablePictureInPicture
          // poster={posterImage}
          style={{
            display: showFallback ? "none" : "block",
          }}
        />
        {showFallback && posterImage && (
          <FallbackImage src={posterImage} alt="Video preview" />
        )}
        {children}
      </VideoContainer>
    );
  }
);

VideoIllustration.displayName = "VideoIllustration";

export default VideoIllustration;
